export class SoundManager {
  private ctx: AudioContext | null = null;
  private masterGain: GainNode | null = null;
  private volume: number = 1;
  private isMuted: boolean = false;
  private ambientSource: AudioBufferSourceNode | null = null;

  constructor() {}

  private ensureContext(): AudioContext {
    if (!this.ctx) {
      this.ctx = new AudioContext();
      this.masterGain = this.ctx.createGain();
      this.masterGain.gain.value = this.isMuted ? 0 : this.volume;
      this.masterGain.connect(this.ctx.destination);
    }
    if (this.ctx.state === "suspended") {
      this.ctx.resume();
    }
    return this.ctx;
  }

  private getMaster(): GainNode {
    this.ensureContext();
    return this.masterGain!;
  }

  private createNoiseBuffer(duration: number): AudioBuffer {
    const ctx = this.ensureContext();
    const sampleRate = ctx.sampleRate;
    const length = Math.floor(sampleRate * duration);
    const buffer = ctx.createBuffer(1, length, sampleRate);
    const data = buffer.getChannelData(0);
    for (let i = 0; i < length; i++) {
      data[i] = Math.random() * 2 - 1;
    }
    return buffer;
  }

  private createBrownNoiseBuffer(duration: number): AudioBuffer {
    const ctx = this.ensureContext();
    const sampleRate = ctx.sampleRate;
    const length = Math.floor(sampleRate * duration);
    const buffer = ctx.createBuffer(1, length, sampleRate);
    const data = buffer.getChannelData(0);
    let last = 0;
    for (let i = 0; i < length; i++) {
      const white = Math.random() * 2 - 1;
      last = (last + 0.02 * white) / 1.02;
      data[i] = last * 3.5;
    }
    return buffer;
  }

  playBreak(): void {
    const ctx = this.ensureContext();
    const master = this.getMaster();
    const t = ctx.currentTime;

    const buffer = this.createNoiseBuffer(0.15);
    const source = ctx.createBufferSource();
    source.buffer = buffer;

    const bandpass = ctx.createBiquadFilter();
    bandpass.type = "bandpass";
    bandpass.frequency.setValueAtTime(2000, t);
    bandpass.frequency.exponentialRampToValueAtTime(400, t + 0.1);
    bandpass.Q.value = 2;

    const gain = ctx.createGain();
    gain.gain.setValueAtTime(0.4, t);
    gain.gain.exponentialRampToValueAtTime(0.001, t + 0.1);

    source.connect(bandpass);
    bandpass.connect(gain);
    gain.connect(master);

    source.start(t);
    source.stop(t + 0.15);
  }

  playPlace(): void {
    const ctx = this.ensureContext();
    const master = this.getMaster();
    const t = ctx.currentTime;

    const osc = ctx.createOscillator();
    osc.type = "sine";
    osc.frequency.setValueAtTime(200, t);
    osc.frequency.exponentialRampToValueAtTime(120, t + 0.05);

    const gain = ctx.createGain();
    gain.gain.setValueAtTime(0.35, t);
    gain.gain.exponentialRampToValueAtTime(0.001, t + 0.05);

    osc.connect(gain);
    gain.connect(master);

    osc.start(t);
    osc.stop(t + 0.05);
  }

  playStep(): void {
    const ctx = this.ensureContext();
    const master = this.getMaster();
    const t = ctx.currentTime;

    const buffer = this.createNoiseBuffer(0.08);
    const source = ctx.createBufferSource();
    source.buffer = buffer;

    const pitchVariation = 0.8 + Math.random() * 0.4;
    source.playbackRate.value = pitchVariation;

    const lowpass = ctx.createBiquadFilter();
    lowpass.type = "lowpass";
    lowpass.frequency.value = 400;
    lowpass.Q.value = 1;

    const gain = ctx.createGain();
    gain.gain.setValueAtTime(0.15, t);
    gain.gain.exponentialRampToValueAtTime(0.001, t + 0.06);

    source.connect(lowpass);
    lowpass.connect(gain);
    gain.connect(master);

    source.start(t);
    source.stop(t + 0.08);
  }

  playJump(): void {
    const ctx = this.ensureContext();
    const master = this.getMaster();
    const t = ctx.currentTime;

    const osc = ctx.createOscillator();
    osc.type = "sine";
    osc.frequency.setValueAtTime(150, t);
    osc.frequency.exponentialRampToValueAtTime(300, t + 0.1);

    const gain = ctx.createGain();
    gain.gain.setValueAtTime(0.2, t);
    gain.gain.exponentialRampToValueAtTime(0.001, t + 0.1);

    osc.connect(gain);
    gain.connect(master);

    osc.start(t);
    osc.stop(t + 0.1);
  }

  playLand(): void {
    const ctx = this.ensureContext();
    const master = this.getMaster();
    const t = ctx.currentTime;

    const osc = ctx.createOscillator();
    osc.type = "sine";
    osc.frequency.setValueAtTime(80, t);
    osc.frequency.exponentialRampToValueAtTime(40, t + 0.08);

    const gain = ctx.createGain();
    gain.gain.setValueAtTime(0.3, t);
    gain.gain.exponentialRampToValueAtTime(0.001, t + 0.08);

    osc.connect(gain);
    gain.connect(master);

    osc.start(t);
    osc.stop(t + 0.08);
  }

  playChat(): void {
    const ctx = this.ensureContext();
    const master = this.getMaster();
    const t = ctx.currentTime;

    const osc1 = ctx.createOscillator();
    osc1.type = "sine";
    osc1.frequency.value = 600;

    const gain1 = ctx.createGain();
    gain1.gain.setValueAtTime(0.25, t);
    gain1.gain.setValueAtTime(0.25, t + 0.035);
    gain1.gain.exponentialRampToValueAtTime(0.001, t + 0.04);

    osc1.connect(gain1);
    gain1.connect(master);
    osc1.start(t);
    osc1.stop(t + 0.04);

    const osc2 = ctx.createOscillator();
    osc2.type = "sine";
    osc2.frequency.value = 800;

    const gain2 = ctx.createGain();
    gain2.gain.setValueAtTime(0.001, t);
    gain2.gain.setValueAtTime(0.25, t + 0.04);
    gain2.gain.setValueAtTime(0.25, t + 0.075);
    gain2.gain.exponentialRampToValueAtTime(0.001, t + 0.08);

    osc2.connect(gain2);
    gain2.connect(master);
    osc2.start(t + 0.04);
    osc2.stop(t + 0.08);
  }

  playAmbient(): void {
    const ctx = this.ensureContext();
    const master = this.getMaster();

    if (this.ambientSource) {
      try {
        this.ambientSource.stop();
      } catch (_) {}
    }

    const buffer = this.createBrownNoiseBuffer(4);
    const source = ctx.createBufferSource();
    source.buffer = buffer;
    source.loop = true;

    const lowpass = ctx.createBiquadFilter();
    lowpass.type = "lowpass";
    lowpass.frequency.value = 200;
    lowpass.Q.value = 0.5;

    const gain = ctx.createGain();
    gain.gain.value = 0.04;

    source.connect(lowpass);
    lowpass.connect(gain);
    gain.connect(master);

    source.start();
    this.ambientSource = source;
  }

  playNPCGreet(): void {
    const ctx = this.ensureContext();
    const master = this.getMaster();
    const t = ctx.currentTime;

    const osc1 = ctx.createOscillator();
    osc1.type = "sine";
    osc1.frequency.value = 440;

    const gain1 = ctx.createGain();
    gain1.gain.setValueAtTime(0.25, t);
    gain1.gain.setValueAtTime(0.25, t + 0.07);
    gain1.gain.exponentialRampToValueAtTime(0.001, t + 0.08);

    osc1.connect(gain1);
    gain1.connect(master);
    osc1.start(t);
    osc1.stop(t + 0.08);

    const osc2 = ctx.createOscillator();
    osc2.type = "sine";
    osc2.frequency.value = 550;

    const gain2 = ctx.createGain();
    gain2.gain.setValueAtTime(0.001, t);
    gain2.gain.setValueAtTime(0.25, t + 0.08);
    gain2.gain.setValueAtTime(0.25, t + 0.15);
    gain2.gain.exponentialRampToValueAtTime(0.001, t + 0.16);

    osc2.connect(gain2);
    gain2.connect(master);
    osc2.start(t + 0.08);
    osc2.stop(t + 0.16);
  }

  playSpeedBoost(): void {
    const ctx = this.ensureContext();
    const master = this.getMaster();
    const t = ctx.currentTime;

    const osc = ctx.createOscillator();
    osc.type = "sawtooth";
    osc.frequency.setValueAtTime(200, t);
    osc.frequency.exponentialRampToValueAtTime(800, t + 0.3);

    const gain = ctx.createGain();
    gain.gain.setValueAtTime(0.15, t);
    gain.gain.exponentialRampToValueAtTime(0.001, t + 0.3);

    osc.connect(gain);
    gain.connect(master);
    osc.start(t);
    osc.stop(t + 0.3);
  }

  playLaunch(): void {
    const ctx = this.ensureContext();
    const master = this.getMaster();
    const t = ctx.currentTime;

    const osc = ctx.createOscillator();
    osc.type = "sine";
    osc.frequency.setValueAtTime(100, t);
    osc.frequency.exponentialRampToValueAtTime(2000, t + 0.5);

    const gain = ctx.createGain();
    gain.gain.setValueAtTime(0.25, t);
    gain.gain.exponentialRampToValueAtTime(0.001, t + 0.5);

    osc.connect(gain);
    gain.connect(master);
    osc.start(t);
    osc.stop(t + 0.5);
  }

  playPaintSplash(): void {
    const ctx = this.ensureContext();
    const master = this.getMaster();
    const t = ctx.currentTime;

    const buffer = this.createNoiseBuffer(0.1);
    const source = ctx.createBufferSource();
    source.buffer = buffer;

    const bandpass = ctx.createBiquadFilter();
    bandpass.type = "bandpass";
    bandpass.frequency.value = 1500;
    bandpass.Q.value = 3;

    const gain = ctx.createGain();
    gain.gain.setValueAtTime(0.3, t);
    gain.gain.exponentialRampToValueAtTime(0.001, t + 0.08);

    source.connect(bandpass);
    bandpass.connect(gain);
    gain.connect(master);
    source.start(t);
    source.stop(t + 0.1);
  }

  playMusicNote(noteIndex: number): void {
    const ctx = this.ensureContext();
    const master = this.getMaster();
    const t = ctx.currentTime;

    // C4 to C5: C D E F G A B C
    const frequencies = [262, 294, 330, 349, 392, 440, 494, 523];
    const freq = frequencies[noteIndex % frequencies.length];

    const osc = ctx.createOscillator();
    osc.type = "sine";
    osc.frequency.value = freq;

    const gain = ctx.createGain();
    gain.gain.setValueAtTime(0.3, t);
    gain.gain.exponentialRampToValueAtTime(0.001, t + 0.5);

    osc.connect(gain);
    gain.connect(master);
    osc.start(t);
    osc.stop(t + 0.5);
  }

  playFireworkLaunch(): void {
    const ctx = this.ensureContext();
    const master = this.getMaster();
    const t = ctx.currentTime;

    const osc = ctx.createOscillator();
    osc.type = "sine";
    osc.frequency.setValueAtTime(400, t);
    osc.frequency.exponentialRampToValueAtTime(2000, t + 0.8);

    const gain = ctx.createGain();
    gain.gain.setValueAtTime(0.15, t);
    gain.gain.exponentialRampToValueAtTime(0.001, t + 0.8);

    osc.connect(gain);
    gain.connect(master);
    osc.start(t);
    osc.stop(t + 0.8);
  }

  playFireworkExplode(): void {
    const ctx = this.ensureContext();
    const master = this.getMaster();
    const t = ctx.currentTime;

    const buffer = this.createNoiseBuffer(0.4);
    const source = ctx.createBufferSource();
    source.buffer = buffer;

    const bandpass = ctx.createBiquadFilter();
    bandpass.type = "bandpass";
    bandpass.frequency.setValueAtTime(3000, t);
    bandpass.frequency.exponentialRampToValueAtTime(300, t + 0.3);
    bandpass.Q.value = 1;

    const gain = ctx.createGain();
    gain.gain.setValueAtTime(0.35, t);
    gain.gain.exponentialRampToValueAtTime(0.001, t + 0.35);

    source.connect(bandpass);
    bandpass.connect(gain);
    gain.connect(master);
    source.start(t);
    source.stop(t + 0.4);
  }

  playFuse(): void {
    const ctx = this.ensureContext();
    const master = this.getMaster();
    const t = ctx.currentTime;

    const buffer = this.createNoiseBuffer(3.0);
    const source = ctx.createBufferSource();
    source.buffer = buffer;

    const highpass = ctx.createBiquadFilter();
    highpass.type = "highpass";
    highpass.frequency.value = 4000;

    const gain = ctx.createGain();
    gain.gain.setValueAtTime(0.08, t);
    gain.gain.setValueAtTime(0.12, t + 2);
    gain.gain.exponentialRampToValueAtTime(0.001, t + 3);

    source.connect(highpass);
    highpass.connect(gain);
    gain.connect(master);
    source.start(t);
    source.stop(t + 3.0);
  }

  playExplosion(): void {
    const ctx = this.ensureContext();
    const master = this.getMaster();
    const t = ctx.currentTime;

    // Deep boom
    const osc = ctx.createOscillator();
    osc.type = "sine";
    osc.frequency.setValueAtTime(60, t);
    osc.frequency.exponentialRampToValueAtTime(20, t + 0.5);

    const oscGain = ctx.createGain();
    oscGain.gain.setValueAtTime(0.4, t);
    oscGain.gain.exponentialRampToValueAtTime(0.001, t + 0.5);

    osc.connect(oscGain);
    oscGain.connect(master);
    osc.start(t);
    osc.stop(t + 0.5);

    // Noise burst
    const buffer = this.createNoiseBuffer(0.3);
    const source = ctx.createBufferSource();
    source.buffer = buffer;

    const lowpass = ctx.createBiquadFilter();
    lowpass.type = "lowpass";
    lowpass.frequency.setValueAtTime(2000, t);
    lowpass.frequency.exponentialRampToValueAtTime(200, t + 0.25);

    const noiseGain = ctx.createGain();
    noiseGain.gain.setValueAtTime(0.35, t);
    noiseGain.gain.exponentialRampToValueAtTime(0.001, t + 0.3);

    source.connect(lowpass);
    lowpass.connect(noiseGain);
    noiseGain.connect(master);
    source.start(t);
    source.stop(t + 0.3);
  }

  playTeleport(): void {
    const ctx = this.ensureContext();
    const master = this.getMaster();
    const t = ctx.currentTime;

    const osc = ctx.createOscillator();
    osc.type = "sine";
    osc.frequency.setValueAtTime(800, t);
    osc.frequency.exponentialRampToValueAtTime(200, t + 0.15);
    osc.frequency.exponentialRampToValueAtTime(1200, t + 0.3);

    const gain = ctx.createGain();
    gain.gain.setValueAtTime(0.25, t);
    gain.gain.exponentialRampToValueAtTime(0.001, t + 0.3);

    osc.connect(gain);
    gain.connect(master);
    osc.start(t);
    osc.stop(t + 0.3);
  }

  setVolume(v: number): void {
    this.volume = Math.max(0, Math.min(1, v));
    if (this.masterGain && !this.isMuted) {
      this.masterGain.gain.value = this.volume;
    }
  }

  mute(): void {
    this.isMuted = true;
    if (this.masterGain) {
      this.masterGain.gain.value = 0;
    }
  }

  unmute(): void {
    this.isMuted = false;
    if (this.masterGain) {
      this.masterGain.gain.value = this.volume;
    }
  }
}
